import { useState, useEffect, useCallback } from 'react';
import { X, Search, LogIn, LogOut, ChevronLeft, AlertCircle, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import SelfieCamera from './SelfieCamera';

interface TeamMember {
  userId: string;
  userBranchMappingId: string;
  name: string;
  displayName?: string;
  email?: string;
  phoneNo?: string;
  role: string;
  branchId: string;
  branchName: string;
}

type ActionType = 'checkin' | 'checkout';
type Step = 'select-member' | 'select-action' | 'camera' | 'saving';

interface StaffAttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  trainer: 'Trainer',
  staff: 'Staff'
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  trainer: 'bg-blue-100 text-blue-700',
  staff: 'bg-gray-100 text-gray-700'
};

export default function StaffAttendanceModal({ isOpen, onClose }: StaffAttendanceModalProps) {
  const [step, setStep] = useState<Step>('select-member');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep('select-member');
    setSearch('');
    setSelectedMember(null);
    setActionType(null);
    setIsSubmitting(false);
    setSubmitError(null);
    setCameraError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }
    fetchMembers();
  }, [isOpen]);

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const res = await fetch(`${API_BASE_URL}/api/team-attendance/team-members`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const json = await res.json();
      if (json.success) {
        setMembers(json.data);
      }
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleMemberSelect = (member: TeamMember) => {
    setSelectedMember(member);
    setStep('select-action');
  };

  const handleActionSelect = (action: ActionType) => {
    setActionType(action);
    setStep('camera');
    setCameraError(null);
  };

  const handleCapture = async (blob: Blob) => {
    if (!selectedMember || !actionType || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setStep('saving');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('photo', blob, 'selfie.jpg');
      formData.append('teamMemberId', selectedMember.userId);
      formData.append('actionType', actionType);

      const res = await fetch(`${API_BASE_URL}/api/team-attendance/selfie-action`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
        body: formData
      });

      const json = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          setSubmitError(json.message || json.error);
        } else {
          setSubmitError(json.error || 'Something went wrong. Please try again.');
        }
        setStep('select-action');
        return;
      }

      toast.success(json.message || `${actionType === 'checkin' ? 'Checked in' : 'Checked out'} successfully`);
      onClose();
    } catch {
      setSubmitError('Upload failed. Please check your connection and try again.');
      setStep('select-action');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 'select-action') {
      setSelectedMember(null);
      setStep('select-member');
    } else if (step === 'camera') {
      setActionType(null);
      setStep('select-action');
    }
    setSubmitError(null);
    setCameraError(null);
  };

  const filteredMembers = members.filter(m => {
    const q = search.toLowerCase();
    return (
      (m.name || '').toLowerCase().includes(q) ||
      (m.displayName || '').toLowerCase().includes(q) ||
      (m.role || '').toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step !== 'select-member' && step !== 'saving' && (
              <button
                onClick={handleBack}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                aria-label="Back"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <div>
              <h2 className="text-base font-semibold text-gray-900">Staff Check-in / Check-out</h2>
              {selectedMember && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedMember.displayName || selectedMember.name}
                  {actionType && ` · ${actionType === 'checkin' ? 'Check-in' : 'Check-out'}`}
                </p>
              )}
            </div>
          </div>
          {step !== 'saving' && (
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 text-gray-400" aria-label="Close">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Step indicators */}
        <div className="flex gap-1 px-5 py-2">
          {(['select-member', 'select-action', 'camera'] as Step[]).map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                step === s || (step === 'saving' && i === 2)
                  ? 'bg-black'
                  : ['select-member', 'select-action', 'camera'].indexOf(step) > i
                  ? 'bg-[#AAD816]'
                  : 'bg-zinc-200'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">

          {/* Step 1: Select Member */}
          {step === 'select-member' && (
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="px-5 py-2">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or role…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#AAD816] bg-zinc-50"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 pb-4">
                {loadingMembers ? (
                  <div className="flex justify-center py-10">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="text-center py-10 text-gray-500 text-sm">No team members found</div>
                ) : (
                  <ul className="space-y-1">
                    {filteredMembers.map(member => (
                      <li key={member.userId}>
                        <button
                          onClick={() => handleMemberSelect(member)}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[#AAD816]/10 text-left transition-colors"
                        >
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#AAD816] to-[#8AAB12] flex items-center justify-center text-black font-semibold text-sm flex-shrink-0">
                            {(member.displayName || member.name || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {member.displayName || member.name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{member.branchName}</div>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-600'}`}>
                            {ROLE_LABELS[member.role] || member.role}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select Action */}
          {step === 'select-action' && selectedMember && (
            <div className="flex flex-col gap-4 px-5 py-6">
              {submitError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{submitError}</span>
                </div>
              )}

              <p className="text-sm text-gray-600 text-center">
                What would you like to record for{' '}
                <span className="font-medium text-gray-900">
                  {selectedMember.displayName || selectedMember.name}
                </span>?
              </p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleActionSelect('checkin')}
                  className="flex flex-col items-center gap-3 p-5 border-2 border-green-200 rounded-2xl hover:border-green-400 hover:bg-green-50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <LogIn size={22} className="text-green-600" />
                  </div>
                  <span className="text-sm font-semibold text-green-700">Check In</span>
                </button>
                <button
                  onClick={() => handleActionSelect('checkout')}
                  className="flex flex-col items-center gap-3 p-5 border-2 border-orange-200 rounded-2xl hover:border-orange-400 hover:bg-orange-50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <LogOut size={22} className="text-orange-600" />
                  </div>
                  <span className="text-sm font-semibold text-orange-700">Check Out</span>
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Camera */}
          {step === 'camera' && (
            <div className="flex flex-col w-full h-[70vh] sm:h-[400px] min-h-0 px-4 pb-4 pt-2">
              {cameraError ? (
                <div className="flex flex-col items-center gap-4 py-10 px-4 text-center">
                  <AlertCircle size={36} className="text-red-400" />
                  <p className="text-sm text-gray-700">{cameraError}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setCameraError(null); }}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
                    >
                      <RotateCcw size={14} /> Try Again
                    </button>
                    <button onClick={handleBack} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                <SelfieCamera
                  isOpen={step === 'camera'}
                  instruction="Please smile and capture"
                  onCapture={handleCapture}
                  onClose={handleBack}
                  onError={msg => setCameraError(msg)}
                />
              )}
            </div>
          )}

          {/* Step 4: Saving */}
          {step === 'saving' && (
            <div className="flex flex-col items-center justify-center gap-4 py-16 px-6">
              <div className="w-12 h-12 border-3 border-[#AAD816] border-t-transparent rounded-full animate-spin" style={{ borderWidth: 3 }} />
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900">Uploading selfie…</p>
                <p className="text-xs text-gray-500 mt-1">Recording attendance for {selectedMember?.displayName || selectedMember?.name}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
